import { AdminTable } from './shared/AdminDataGrid';
/**
 * Tool Stats List Component
 * Displays aggregate statistics for AI therapist tool usage
 */

import { useState, useEffect, useCallback } from 'react';
import { useNotify, Title } from 'react-admin';
import {
  Typography,
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  LinearProgress,
  Button,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SpeedIcon from '@mui/icons-material/Speed';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import { brand, semantic } from '@rekindle/shared/theme';
import { adminApi } from '../providers/dataProvider';
import { logger } from '../utils/logger';
import { StatCard } from './shared';

interface ToolStats {
  toolName: string;
  totalCalls: number;
  successRate: number;
  avgDurationMs: number;
}

interface ToolStatsResponse {
  summary: {
    totalCalls: number;
    successRate: number;
    avgDurationMs: number;
    last24h: number;
    last7d: number;
    last30d: number;
  };
  byTool: ToolStats[];
}

type SortField = 'toolName' | 'totalCalls' | 'successRate' | 'avgDurationMs';

/**
 * Format tool name for display
 * suggest_activity → Suggest Activity
 */
function formatToolName(toolName: string): string {
  return toolName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get color for success rate
 */
function getSuccessRateColor(rate: number): 'success' | 'warning' | 'error' {
  if (rate >= 90) return 'success';
  if (rate >= 70) return 'warning';
  return 'error';
}

export function ToolStatsList() {
  const [data, setData] = useState<ToolStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortField>('totalCalls');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const notify = useNotify();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminApi.getToolStats();
      setData(result);
    } catch (error) {
      notify('Failed to load tool stats', { type: 'error' });
      logger.error('Failed to fetch tool stats:', error);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (field: SortField) => {
    if (sort === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field);
      setOrder('desc');
    }
  };

  const sortedTools = data?.byTool ? [...data.byTool].sort((a, b) => {
    const aVal = a[sort];
    const bVal = b[sort];
    
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return order === 'asc' 
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }
    
    const aNum = typeof aVal === 'number' ? aVal : 0;
    const bNum = typeof bVal === 'number' ? bVal : 0;
    return order === 'asc' ? aNum - bNum : bNum - aNum;
  }) : [];

  return (
    <Box>
      <Title title="Tool Stats" />

      <Box>
        {/* Header with Refresh Button */}
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {data && (
          <>
            {/* Summary Stats */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Overall Statistics
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} component="div">
                <StatCard
                  title="Total Calls"
                  value={data.summary.totalCalls.toLocaleString()}
                  icon={<BuildCircleOutlinedIcon sx={{ color: 'white' }} />}
                  color={brand.primary}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} component="div">
                <StatCard
                  title="Success Rate"
                  value={`${data.summary.successRate.toFixed(1)}%`}
                  icon={<CheckCircleIcon sx={{ color: 'white' }} />}
                  color={semantic.success}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }} component="div">
                <StatCard
                  title="Avg Duration"
                  value={`${data.summary.avgDurationMs.toFixed(0)} ms`}
                  icon={<SpeedIcon sx={{ color: 'white' }} />}
                  color={semantic.info}
                />
              </Grid>
            </Grid>

            {/* Activity Stats */}
            <Typography variant="h6" gutterBottom>
              Activity Breakdown
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, sm: 4 }} component="div">
                <StatCard
                  title="Last 24h"
                  value={data.summary.last24h.toLocaleString()}
                  icon={<TodayIcon sx={{ color: 'white' }} />}
                  color={semantic.success}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} component="div">
                <StatCard
                  title="Last 7d"
                  value={data.summary.last7d.toLocaleString()}
                  icon={<DateRangeIcon sx={{ color: 'white' }} />}
                  color={semantic.warning}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} component="div">
                <StatCard
                  title="Last 30d"
                  value={data.summary.last30d.toLocaleString()}
                  icon={<CalendarMonthIcon sx={{ color: 'white' }} />}
                  color={semantic.info}
                />
              </Grid>
            </Grid>
          </>
        )}

        {/* Per-Tool Table */}
        <Typography variant="h6" gutterBottom>
          By Tool
        </Typography>
        <TableContainer component={Paper}>
          {loading && <LinearProgress />}
          <AdminTable>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sort === 'toolName'}
                    direction={sort === 'toolName' ? order : 'asc'}
                    onClick={() => handleSort('toolName')}
                  >
                    Tool Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sort === 'totalCalls'}
                    direction={sort === 'totalCalls' ? order : 'desc'}
                    onClick={() => handleSort('totalCalls')}
                  >
                    Total Calls
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sort === 'successRate'}
                    direction={sort === 'successRate' ? order : 'desc'}
                    onClick={() => handleSort('successRate')}
                  >
                    Success Rate
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sort === 'avgDurationMs'}
                    direction={sort === 'avgDurationMs' ? order : 'desc'}
                    onClick={() => handleSort('avgDurationMs')}
                  >
                    Avg Duration (ms)
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTools.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      No tool usage data available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {sortedTools.map((tool) => (
                <TableRow key={tool.toolName} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {formatToolName(tool.toolName)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {tool.totalCalls.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${tool.successRate.toFixed(1)}%`}
                      size="small"
                      color={getSuccessRateColor(tool.successRate)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {tool.avgDurationMs.toFixed(0)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </AdminTable>
        </TableContainer>
      </Box>
    </Box>
  );
}
